import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Undo } from '@vy/ui-design-system';
import { getCurrencySymbol } from '@vy/services';
import type { LineItem } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { get, useFormContext } from 'react-hook-form';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';
import { useUndoLineItemPriceOverride } from '../../../../../../hooks/requests/use-undo-line-item-price-override';
import { runMutation } from '../../../../../../utils/vy-mutation';
import { useDynamicInvoice } from '../../../dynamic-invoice-context';

export type LineItemFormFieldConfig = {
  nameFieldPath: string;
  descriptionFieldPath: string;
  priceFieldPath: string;
  isNameRequired?: boolean;
  taxRateFieldPath: string;
};

type SharedLineItemFormContentProps = {
  fieldConfig: LineItemFormFieldConfig;
  lineItem: LineItem;
  onUndoSuccess?: () => void;
};

export const SharedLineItemFormContent: React.FC<SharedLineItemFormContentProps> = ({
  fieldConfig,
  lineItem,
  onUndoSuccess,
}) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();
  const revenueBreakdownTaxRate = lineItem.saleTax?.finalSaleTax ?? 0;

  const { nameFieldPath, descriptionFieldPath, priceFieldPath, isNameRequired } = fieldConfig;

  const nameRegister = register(nameFieldPath);

  const descriptionRegister = register(descriptionFieldPath);
  const priceRegister = register(priceFieldPath, {
    valueAsNumber: true,
  });

  const { dynamicInvoiceData } = useDynamicInvoice();
  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;

  const undoLineItemPriceOverrideMutation = useUndoLineItemPriceOverride();

  const hasPriceOverride = isNotNullOrUndefined(lineItem.modifiedPrice);

  const handleUndoPriceOverride = async () => {
    await runMutation(
      undoLineItemPriceOverrideMutation,
      { lineItemId: lineItem.id },
      {
        pending: 'Reverting to original price...',
        success: 'Price reverted to original',
        error: 'Failed to revert price',
      },
      {
        onSuccess: (response) => {
          setValue(priceFieldPath, response.lineItem.revenueBreakdown.total);
          onUndoSuccess?.();
        },
      },
    );
  };

  const renderPriceEndAdornment = () => {
    if (!hasPriceOverride) {
      return undefined;
    }

    return (
      <InputAdornment position="end">
        <VyTooltip tooltipContent="Revert to original price" placement="top" arrow>
          <IconButton
            size="small"
            onClick={handleUndoPriceOverride}
            disabled={undoLineItemPriceOverrideMutation.isLoading}
          >
            <Undo />
          </IconButton>
        </VyTooltip>
      </InputAdornment>
    );
  };

  return (
    <Stack
      sx={{
        padding: "10px",
        gap: 2
      }}>
      <TextField
        {...nameRegister}
        label="Product name"
        placeholder="Product name"
        size="medium"
        required={isNameRequired}
        fullWidth
        error={isNotNullOrUndefined(get(errors, nameRegister.name))}
        helperText={get(errors, nameRegister.name)?.message}
      />
      <TextField
        {...descriptionRegister}
        label="Product description"
        placeholder="Product description"
        size="medium"
        fullWidth
        error={isNotNullOrUndefined(get(errors, descriptionRegister.name))}
        helperText={get(errors, descriptionRegister.name)?.message}
      />
      <Stack direction="row" sx={{
        gap: 2
      }}>
        {/* TODO: Uncomment this when we have a way to display the tax efficiently */}
        {/* {isTaxIntegrationsConnected && (
          <Box flexGrow={1}>
            <TextField
              {...register(taxRateFieldPath, {
                setValueAs: (value) => {
                  console.log(value);
                  const trimmed = String(value)?.trim();

                  if (isNullOrUndefined(trimmed) || isEmptyString(trimmed)) {
                    return null;
                  }

                  return Number(trimmed);
                },
              })}
              label="Sales tax"
              placeholder={`Sales tax ${revenueBreakdownTaxRate}`}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>
                  ),
                },
              }}
              size="medium"
              fullWidth
              error={isNotNullOrUndefined(get(errors, taxRateFieldPath))}
              helperText={get(errors, taxRateFieldPath)?.message}
            />
          </Box>
        )} */}

        <Box sx={{
          flexGrow: 1
        }}>
          <TextField
            {...priceRegister}
            label="Amount"
            placeholder="Amount"
            type="number"
            fullWidth
            size="medium"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>
                ),
                endAdornment: renderPriceEndAdornment(),
              },
            }}
            error={isNotNullOrUndefined(get(errors, priceRegister.name))}
            helperText={get(errors, priceRegister.name)?.message}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginTop: "11px"
            }}>
            This amount doesn’t include Sales tax. The Sales tax is {revenueBreakdownTaxRate}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};
